import {Injectable} from '@angular/core';

import {AuthService} from '@app/auth/auth.service';
import {User} from '@app/shared/user.service';
import * as Sentry from "@sentry/angular";

import {BehaviorSubject, Subject} from 'rxjs';
import {filter} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

export enum MessageType {
    // User
    RegisterUser,

    // Review
    ReviewChange,
    ReviewState,
    ReviewJoin,
    ReviewSync,

    // Asset
    AssetSubscription,

    // Annotation
    AnnotationChange,

    // Stage
    UserCursor,
    StrokeHistory,
}

export enum ConnectedState {
    Disconnected,
    Connected,
    UserRegistered,
}

export class BaseMessage {
    Type: MessageType;
}

const PongValue = '2';

@Injectable({
    providedIn: 'root',
})
export class WebSocketService {
    private _socket: WebSocket;
    private _user: User;
    private _heartbeatTimer: NodeJS.Timeout;

    private connectStateSubject = new BehaviorSubject<ConnectedState>(ConnectedState.Disconnected);
    private messageSubject = new Subject<BaseMessage>();

    public connectState$ = this.connectStateSubject.asObservable();
    public message$ = this.messageSubject.asObservable();

    get isConnected(): boolean {
        return this._socket && this._socket.readyState === WebSocket.OPEN;
    }

    constructor(private authService: AuthService) {
        // FIXME: What if i log out and switch users?
        this.authService.user
            .pipe(filter((i) => Boolean(i)))
            .subscribe((user) => this.connect(user));
    }

    public connect(user: User): void {
        if (user.id === user.id && this.isConnected) {
            // The user didn't change so no need to make a new connection
            return;
        }

        if (this._socket) {
            this._socket.close();
        }

        this._user = user;

        const host = environment.websocketHost;
        const url = `wss://${host}/ws/`;

        this._socket = new WebSocket(url);
        this._socket.onopen = this.webSocketOpen.bind(this);
        this._socket.onclose = this.webSocketClose.bind(this);
        this._socket.onerror = this.webSocketError.bind(this);
        this._socket.onmessage = this.webSocketMessage.bind(this);
    }

    public disconnect(): void {
        this._socket.close();
    }

    public send(data: object, type: MessageType) {
        if (!this.isConnected) {
            // We should surface this state issue
            return;
        }

        data['Type'] = type;
        this._socket.send(JSON.stringify(data));
    }

    private webSocketOpen() {
        this.connectStateSubject.next(ConnectedState.Connected);

        // Send user info immediately
        this.send(this._user, MessageType.RegisterUser);

        clearInterval(this._heartbeatTimer);
        this._heartbeatTimer = setInterval(() => {
            this._socket.send('1');
        }, 10 * 1000);
    }

    private webSocketClose(event: CloseEvent): void {
        this.connectStateSubject.next(ConnectedState.Disconnected);

        clearInterval(this._heartbeatTimer);

        if (event.code === 1006) {
            Sentry.captureMessage('Websocket Closed', {
                extra: {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                    readyState: this._socket.readyState,
                }
            })
        }
    }

    private webSocketError(error): void {
        Sentry.captureMessage("Websocket error", {
            extra: {
                user: this._user.email,
                readyState: this._socket.readyState
            }
        });
    }

    private webSocketMessage(event: MessageEvent): void {
        if (event.data === PongValue) {
            return;
        }

        const message = JSON.parse(event.data) as BaseMessage;

        if (message.Type == MessageType.RegisterUser) {
            this.connectStateSubject.next(ConnectedState.UserRegistered);
        }

        this.messageSubject.next(message);
    }
}
